import { PURGE_REFUSED_REASON } from '@modules/common/pipes/can-purge.pipe';

// Common
import { type HttpContext } from '@angular/common/http';
import {
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';

// RX
import { merge, of, Subject } from 'rxjs';
import { filter, switchMap, map, tap, takeUntil, pairwise, startWith } from 'rxjs/operators';

// Types
import { SortOrder } from '@modules/common/types/sort-order';
import { type PendingSubtask, Task } from '@modules/tasks/types/task';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';

// Services
import { TasksService } from '@modules/tasks/services/tasks.service';
import { TaskingAppSettingsService } from '@modules/tasks/services/settings.service';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';

@Component({
  selector: 'app-task-form-subtasks',
  templateUrl: './task-form-subtasks.component.html',
  styleUrls: ['./task-form-subtasks.component.less'],
  standalone: false,
})
export class TaskFormSubtasksComponent implements OnInit, OnChanges, OnDestroy {
  protected readonly PURGE_REFUSED_REASON = PURGE_REFUSED_REASON;

  // Inputs
  @Input() task: Task;
  @Input() filters: TasksFilters;
  @Input() triggerRefresh: Subject<void>;
  @Input() requestContext: HttpContext | undefined;
  @Input() pendingSubtasks: PendingSubtask[] = [];

  // Outputs
  public readonly pendingSubtasksChange = output<PendingSubtask[]>();

  // Public
  public subtasks: Task[] = [];
  public title = '';
  /**
   * Subtasks typed and sent but not back from the server yet: drawn at the list's end straight away, so Enter
   * never leaves an empty row showing its "Subtask" placeholder in the meantime (Johanna, 2026-10-21).
   */
  public saving: { id: string; title: string }[] = [];
  /** stitch-app-shell subtasks: "+ Add a new subtask" opens an empty row at the end to type the subtask in. */
  public adding = false;
  public showIssueKey = false;
  private readonly newSubtaskInput = viewChild<ElementRef<HTMLInputElement>>('newSubtaskInput');

  // Private
  private alive = new Subject<void>();
  private tasksUpdated = new Subject<string>();
  private triggerRefreshUpdated = new Subject<void>();
  private readonly editSubtask$ = new Subject<Task>();
  private readonly stitchEvents = inject(StitchEventsService);
  /** Rows moved to Trash or deleted stay out, even while a lagging read still lists them (#2067). */
  private readonly removed = new Set<string>();
  /** Whether the list shown is the parent's Trash list; `removed` belongs to one list only. */
  private shownDeleted: boolean | undefined;

  /**
   * Constructor
   */

  constructor(
    private tasksService: TasksService,
    private settingsService: TaskingAppSettingsService,
  ) {}

  ngOnInit() {
    merge(
      this.tasksService.getRefreshRequired(),
      this.triggerRefreshUpdated.pipe(switchMap(() => this.triggerRefresh || of())),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.tasksUpdated.next(null);
      });

    this.tasksUpdated
      .pipe(
        startWith(this.task?.id),
        pairwise(),
        tap(([id1, id2]) => {
          const deleted = !!this.task?.deleted;
          if (deleted !== this.shownDeleted) {
            this.shownDeleted = deleted;
            this.removed.clear();
          }
          if (id1 === id2) return;
          this.subtasks = [];
          this.removed.clear();
        }),
        filter(() => !!this.task && !!this.task.id),
        switchMap(() =>
          this.tasksService.search(
            {
              ...this.filters,
              parentsIds: [this.task.id],
              // tasking-app returns live and trashed rows alike when `deleted` is left out (#2067).
              deleted: !!this.task.deleted,
              limit: 1000,
              sortBy: 'position',
              sortOrder: SortOrder.ASC,
            },
            { requestContext: this.requestContext },
          ),
        ),
        map(({ items }) => items),
      )
      .subscribe((tasks: Task[]) => {
        this.subtasks = tasks.filter((row) => !this.removed.has(rowKey(row)));
        this.saving = this.saving.filter((row) => !this.subtasks.some((subtask) => subtask.id === row.id));
        if (this.pendingSubtasks.length > 0) {
          this.pendingSubtasks = [];
          this.pendingSubtasksChange.emit(this.pendingSubtasks);
        }
      });

    // A trash succeeds before the list re-reads: the row leaves at once, here and for a trash made elsewhere.
    this.stitchEvents
      .getEvents(() => this.owners(), StitchType.TASK, [
        SignalStitchEventType.MOVED_TO_TRASH,
        SignalStitchEventType.DELETED,
        SignalStitchEventType.RESTORED_FROM_TRASH,
      ])
      .pipe(takeUntil(this.alive))
      .subscribe((event) => {
        for (const id of event.ids) {
          const key = rowKey({ accountId: event.spaceId, id });
          if (event.eventType === SignalStitchEventType.RESTORED_FROM_TRASH) this.removed.delete(key);
          // Trash hides a row from the live list only; a parent's Trash list keeps its trashed rows.
          else if (event.eventType === SignalStitchEventType.DELETED || !this.task?.deleted) this.removed.add(key);
        }
        this.subtasks = this.subtasks.filter((row) => !this.removed.has(rowKey(row)));
      });

    this.settingsService
      .listAll()
      .pipe(takeUntil(this.alive))
      .subscribe(({ issueKeyEnabled }) => (this.showIssueKey = issueKeyEnabled));

    this.tasksUpdated.next(null);
    this.triggerRefreshUpdated.next();

    const updateFeedbackConfig = {
      emit: false,
      message: 'Subtask successfully updated',
      requestContext: this.requestContext,
    };
    this.editSubtask$
      .pipe(
        takeUntil(this.alive),
        switchMap((updatedSubtask: Task) => this.tasksService.update(updatedSubtask, updateFeedbackConfig)),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('task' in changes) {
      const prev = changes['task'].previousValue as Task | undefined;
      if (!prev?.id && this.task?.id && this.pendingSubtasks.length > 0) {
        this.pendingSubtasks = [];
        this.pendingSubtasksChange.emit(this.pendingSubtasks);
      }
    }

    if ('task' in changes || 'filters' in changes) {
      this.tasksUpdated.next(this.task?.id);
    }

    if ('triggerRefresh' in changes) {
      this.triggerRefreshUpdated.next();
    }
  }

  private owners(): string[] {
    return [...new Set([this.task?.accountId, ...this.subtasks.map((row) => row.accountId)].filter((id) => !!id))];
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  startAdding(): void {
    this.adding = true;
    setTimeout(() => this.newSubtaskInput()?.nativeElement.focus());
  }

  /** Leaving the new row saves what was typed; an empty row just closes. */
  handleAddBlur(): void {
    this.handleEnterNew();
    this.adding = false;
  }

  cancelAdding(): void {
    this.title = '';
    this.adding = false;
  }

  /** Saves the typed subtask at the end of the list, where its row was; the row stays open for the next one. */
  handleEnterNew(): void {
    if (!this.title || this.title.trim() === '') {
      return;
    }

    if (!this.task?.id) {
      this.pendingSubtasks = [
        ...this.pendingSubtasks,
        { id: crypto.randomUUID(), title: this.title.trim(), position: this.pendingSubtasks.length },
      ];
      this.pendingSubtasksChange.emit(this.pendingSubtasks);
      this.title = '';
      return;
    }

    const last = this.subtasks.at(-1);
    const savingRow: { id: string; title: string } = { id: crypto.randomUUID(), title: this.title.trim() };
    this.saving = [...this.saving, savingRow];
    const newSubtask = new Task({
      parentId: this.task.id,
      projectId: this.task.projectId,
      position: last ? (last.position ?? this.subtasks.length - 1) + 1 : 0,
      title: this.title,
    });

    this.tasksService
      .create(newSubtask, {
        emit: false,
        message: 'Subtask successfully created',
        requestContext: this.requestContext,
      })
      .pipe(takeUntil(this.alive))
      .subscribe({
        next: (created) => {
          // Known by the created task's id from here, so the list's reload replaces the row, not a duplicate.
          savingRow.id = created?.id ?? savingRow.id;
          this.tasksUpdated.next(null);
        },
        error: () => (this.saving = this.saving.filter((row) => row !== savingRow)),
      });

    this.title = '';
  }

  removePendingSubtask(id: string): void {
    this.pendingSubtasks = this.pendingSubtasks.filter((s) => s.id !== id).map((s, i) => ({ ...s, position: i }));
    this.pendingSubtasksChange.emit(this.pendingSubtasks);
  }

  handleComplete(item: Task) {
    item.completed = !item.completed;
    this.tasksService.update(item, { emit: false, requestContext: this.requestContext }).pipe(takeUntil(this.alive));
  }

  blurTarget(event: Event): void {
    (event.target as HTMLElement).blur();
  }

  handleEditSubtask(event: Event, subtask: Task): void {
    const input = event.target as HTMLInputElement;
    const title = input.value;
    if (!title || title === subtask.title) {
      return;
    }
    const updatedSubtask = new Task({ ...subtask, title: title });
    this.editSubtask$.next(updatedSubtask);
  }

  handleDeleteSubtask(subtask: Task): void {
    const feedbackConfig = {
      emit: false,
      skipConfirmation: true,
      message: 'Subtask successfully deleted',
      requestContext: this.requestContext,
    };
    this.tasksService.deleteSubtasks([subtask.id], feedbackConfig);
  }
}

function rowKey(row: { accountId?: string; id?: string }): string {
  return `${row.accountId}/${row.id}`;
}
